import { authService } from './auth';

const AI_CONTROLLER_API_BASE = '/api/ai/v1';

export interface AITaskAssignment {
  task_id: string;
  worker_url: string;
  upload_token: string;
  status?: string;
}

export interface CreateTextTaskInput {
  prompt: string;
  taskType?: string;
  service?: string;
  provider?: string;
  model?: string;
  estimatedTokens?: number;
  meta?: Record<string, unknown>;
}

interface WorkerResultPayload {
  status?: string;
  text_result?: string;
  result_text?: string;
  text?: string;
  content?: string;
  message?: string;
  result?: Record<string, unknown>;
  choices?: Array<{ message?: { content?: string } }>;
}

function buildHeaders(): Record<string, string> {
  const token = authService.getSessionToken?.();
  const businessId = authService.getBusinessId?.();

  if (!token) {
    throw new Error('Thiếu session token để gọi ai-controller.');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  if (businessId) headers['X-Business-ID'] = businessId;

  const rawUser = localStorage.getItem('user');
  if (rawUser) {
    try {
      const user = JSON.parse(rawUser);
      if (user?.id) headers['X-User-ID'] = user.id;
      if (user?.telegram_id) headers['X-Telegram-ID'] = String(user.telegram_id);
      if (user?.role_codes?.[0]) headers['X-User-Role'] = user.role_codes[0];
    } catch {
      // Ignore malformed user payload in local storage.
    }
  }

  return headers;
}

function extractText(payload: WorkerResultPayload): string {
  const keys: Array<keyof WorkerResultPayload> = [
    'text_result',
    'result_text',
    'text',
    'content',
    'message',
  ];

  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  if (payload.result && typeof payload.result === 'object') {
    return extractText(payload.result as WorkerResultPayload);
  }

  const first = payload.choices?.[0];
  const content = first?.message?.content;
  if (typeof content === 'string' && content.trim()) {
    return content.trim();
  }

  return '';
}

export async function createTextTask(input: CreateTextTaskInput): Promise<AITaskAssignment> {
  const taskType = input.taskType || 'text.generate';
  const service = input.service || 'text2text';
  const model = input.model || 'gemini-2.0-flash-exp';

  const response = await fetch(`${AI_CONTROLLER_API_BASE}/tasks`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({
      task_type: taskType,
      provider: input.provider || 'gemini',
      model,
      prompt: input.prompt,
      estimated_tokens: input.estimatedTokens,
      parameters: {
        input: {
          prompt: input.prompt,
          service,
          model,
        },
        meta: input.meta || {},
      },
    }),
  });

  const data = (await response.json().catch(() => null)) as
    | AITaskAssignment
    | { error?: string; detail?: string }
    | null;

  if (!response.ok) {
    const detail = data && 'detail' in data && data.detail ? ` - ${data.detail}` : '';
    const message = data && 'error' in data && data.error ? data.error : `AI task lỗi ${response.status}`;
    throw new Error(`${message}${detail}`);
  }

  if (!data || !('task_id' in data) || !('worker_url' in data)) {
    throw new Error('ai-controller không trả task assignment hợp lệ.');
  }

  if (!String(data.worker_url).startsWith('http')) {
    throw new Error(`worker_url không hợp lệ: ${String(data.worker_url)}`);
  }

  return data as AITaskAssignment;
}

export async function pollTextResult(
  workerUrl: string,
  taskId: string,
  intervalMs = 1500,
  timeoutMs = 120000,
): Promise<string | null> {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));

    try {
      const response = await fetch(`${workerUrl}/api/result/${encodeURIComponent(taskId)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      if (response.status === 202) {
        continue;
      }
      if (!response.ok) {
        continue;
      }

      const payload = (await response.json().catch(() => null)) as WorkerResultPayload | null;
      if (!payload) {
        continue;
      }

      const status = String(payload.status || '').toLowerCase();
      if (status === 'failed' || status === 'error') {
        throw new Error('Worker xử lý task thất bại.');
      }
      if (status === 'pending' || status === 'accepted' || status === 'processing') {
        continue;
      }

      const text = extractText(payload);
      if (text) {
        return text;
      }
    } catch {
      // Tunnel network can flap; keep polling until timeout.
    }
  }

  return null;
}
